import React, {useEffect} from "react";
import { useDispatch } from "react-redux";
import { KCheckbox, KDialog, KList, KLoader } from "@kyburz/kdesign-system";
import { Divider, Typography } from "@material-ui/core";
import { setAlert } from "../../../../redux/actions/actions";
import KSelectInfinite from "../../../../components/ui/KSelectInfinite/KSelectInfinite";
import { useStyles } from "./styles";
import useSetState from "../../../../hooks/useTypedSetState";
import { UpdateElements, SchedulePopupData, UpdateType } from "../types";
import KDateTimePicker from "../../../../KDesign-components/KDateTimePicker/KDateTimePicker";
import { buildDatetimeInTZ, formatDateTZ, parseZurichDate } from "../../../../utils/timeUtils";
import { uploadScheduledFWToDevices } from "../../../../apiCalls/config/scheduledFWUpdates";
import { fetchFirmwareOrConfigList } from "../useFwOrConfigList";
import { uploadScheduledConfigToDevices } from "../../../../apiCalls/config/scheduledConfigUpdates";

type IState = {
  startTime: string;
  fromDate: Date;
  endTime: string;
  toDate: Date;
  ignitionOn: boolean;
  ignitionOff: boolean;
  firmwareOrConfigList: UpdateElements[];
  deviceFirmwareOrConfigID: string | undefined;
  loading: boolean;
  page: number;
  hasNextPage: boolean;
}

interface Props {
  data: SchedulePopupData;
  toggleSchedulePopup: (scheduledUpdate?: any) => void;
  onSuccess: () => void;
  startLoading: () => void;
}

const ScheduledUpdatePopup = ({ data, toggleSchedulePopup, onSuccess, startLoading }: Props) => {
  const dispatch = useDispatch();
  const isFirmware: boolean = data.updateType === UpdateType.firmware;
  const classes = useStyles();

  const [{
    startTime,
    fromDate,
    endTime,
    toDate,
    ignitionOn,
    ignitionOff,
    firmwareOrConfigList,
    deviceFirmwareOrConfigID,
    loading,
    page,
    hasNextPage,
  }, setState
  ] = useSetState<IState>({
      startTime: "",
      fromDate:  new Date(),
      endTime: "",
      toDate: new Date(),
      ignitionOn: false,
      ignitionOff: false,
      firmwareOrConfigList: [],
      deviceFirmwareOrConfigID: undefined,
      loading: true,
      page: 1,
      hasNextPage: false,
    });

  const onDateChange = (dates: any) => {
    if (dates.length === 2) {
      const [start, end] = dates;
      setState({
        fromDate: start,
        toDate: end,
        endTime: "00:00",
        startTime: "00:00",
      });
    } else {
      setState({ fromDate: dates, toDate: dates });
    }
  };

  const onTimeChange = (e: any) => {
    setState({ [e.target.name]: e.target.value });
  };

  const handleCheck = (e: any) => {
    setState({ [e.target.name]: e.target.checked });
  }

  useEffect(() => {
    const promise = fetchFirmwareOrConfigList({
      updateType: data.updateType as UpdateType,
      hardwareTypes: data.hardwareTypes,
      simTypes: data.simTypes,
      page: 1,
    });
    if (!promise) return;

    setState({ loading: true, firmwareOrConfigList: [], page: 1, hasNextPage: false });
    promise.then(({ data: list, hasNextPage: nextHasNextPage }) => {
      const preSelected = list.find(x => x.id === data.deviceFirmwareID);
      setState({
        firmwareOrConfigList: list,
        deviceFirmwareOrConfigID: preSelected?.id ?? undefined,
        fromDate: parseZurichDate(data.allowedFrom),
        toDate: parseZurichDate(data.allowedTo),
        startTime: formatDateTZ(data.allowedFrom, "HH:mm", undefined, ""),
        endTime: formatDateTZ(data.allowedTo, "HH:mm", undefined, ""),
        ignitionOn: data.ignitionOn ?? false,
        ignitionOff: data.ignitionOff ?? false,
        loading: false,
        hasNextPage: nextHasNextPage,
        page: 1,
      });
    });
  }, [data.updateType, data.deviceFirmwareID]);

  const loadNextPage = () => {
    if (!hasNextPage || loading) return;

    const nextPage = page + 1;
    const promise = fetchFirmwareOrConfigList({
      updateType: data.updateType as UpdateType,
      hardwareTypes: data.hardwareTypes,
      simTypes: data.simTypes,
      page: nextPage,
    });
    if (!promise) return;

    setState({ loading: true });
    promise.then(({ data: list, hasNextPage: nextHasNextPage }) => {
      setState({
        firmwareOrConfigList: [...firmwareOrConfigList, ...list],
        hasNextPage: nextHasNextPage,
        page: nextPage,
        loading: false,
      });
    });
  };

  const handleUpdate = () => {
    if (!data.deviceIds) return;
    startLoading();
    const dateFromStr: string = buildDatetimeInTZ(fromDate, startTime);
    const dateToStr: string = buildDatetimeInTZ(toDate, endTime);
    if(deviceFirmwareOrConfigID) {
      const req = isFirmware
        ? uploadScheduledFWToDevices(data.deviceIds, Number(deviceFirmwareOrConfigID), dateFromStr, dateToStr, ignitionOn, ignitionOff, data.scheduledUpdateID)
        : uploadScheduledConfigToDevices(data.deviceIds, Number(deviceFirmwareOrConfigID), dateFromStr, dateToStr, ignitionOn, ignitionOff, data.scheduledUpdateID);
      req.then((res: any) => {
        if (res?.data?.status === 200 || res?.data?.status === 201) {
          toggleSchedulePopup();
          const alert = isFirmware
            ? "Successfully scheduled a firmware update"
            : "Successfully scheduled a config update";
          dispatch(setAlert(alert, "success"));
          onSuccess();
        } else {
          dispatch(setAlert(`${res}`, "error"));
          onSuccess();
        }
      });
    }
  };

  const handleFwOrConfigSelect = (element: { label: string; id: string }) => {
    setState({ deviceFirmwareOrConfigID: element?.id });
  };

  const initialLoading = loading && firmwareOrConfigList.length === 0;

  return (
    <KDialog
      size="md"
      title={`Schedule a ${isFirmware ? "firmware" : "config"} update`}
      open={true}
      primaryCTAonClick={handleUpdate}
      primaryCTAColor="primary"
      primaryCTAText={`Update ${data.deviceIds?.length} devices`}
      secondaryCTAonClick={toggleSchedulePopup}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={!deviceFirmwareOrConfigID || initialLoading}
    >
      <div className={classes.container}>
        <div className={classes.dropdownFWSelector}>
          {initialLoading ? (
            <KLoader height="50px" />
          ) : (
            <KSelectInfinite
              placeholder={`Select ${isFirmware ? "firmware" : "config"}`}
              elements={firmwareOrConfigList}
              onSelect={handleFwOrConfigSelect}
              style={{ width: "100%" }}
              value={
                firmwareOrConfigList.find(
                  (x) => x.id.toString() === deviceFirmwareOrConfigID?.toString()
                )?.label ?? ""
              }
              hasNextPage={hasNextPage}
              loadNextPage={loadNextPage}
            />
          )}
        </div>
        <div className={classes.dateTimeContainer}>
          <KDateTimePicker
            onChange={onDateChange}
            setApplyClicked={() => {}}
            onTimeChange={onTimeChange}
            startTime={startTime}
            endTime={endTime}
            fromDate={fromDate}
            toDate={toDate}
            label="Allowed from - Allowed to"
          />
        </div>
        <div className={classes.ignitionContainer}>
          <KCheckbox
            name="ignitionOff"
            label="Ignition OFF only"
            handleChange={(e) => handleCheck(e)}
            checked={ignitionOff}
            style={{ margin: 0 }}
          />
          <KCheckbox
            name="ignitionOn"
            label="Ignition ON only"
            handleChange={(e) => handleCheck(e)}
            checked={ignitionOn}
            style={{ margin: 0 }}
          />
          <Typography variant="body2" color="textSecondary">
            If both unchecked: update does not depend on ignition state
          </Typography>
        </div>
        <div className={classes.listContainer}>
          <Divider className={classes.divider} />
          <Typography variant="body1" color="textSecondary">
            Devices to update:
          </Typography>
          <KList
            elements={data.deviceIds && data.deviceIds[0] === "all"
              ? ["Not possible to select all IMEIs for now. Please filter by customer or select IMEIs manually"]
              : data.deviceIds || []
            }
            height={100}
            selectable={false}
            width="auto"
          />
        </div>
      </div>
    </KDialog>
  );
};

export default ScheduledUpdatePopup;